// Phase 31 (plan 02): per-instance configuration for the reusable chat surface.
//
// REJECTED ALTERNATIVE: module-level mutable globals + setter functions
// (setMediaAuthTokenKey / setMediaProxyBase). Two embeds mounted on the same
// page (e.g. Bayon doc A in tab 1 and doc B in tab 2 sharing the same JS
// context) would clobber each other's config because globals are process-wide.
// React context is per-component-tree, so each <ChatConfigProvider> isolates
// its own values.
//
// Default values match today's technician miniapp behavior so consumers WITHOUT
// a provider keep working unchanged. Only the Bayon embed's entry.tsx wraps a
// non-default provider.

import { createContext, useContext, type ReactNode } from 'react';

export interface ChatConfig {
  /** localStorage key for the auth bearer token. Technician = 'support_auth_token'. */
  authTokenKey: string;
  /** Auth-gated media proxy base path. Technician = '/api/media'. Bayon = '/api/bayon/media'. */
  mediaProxyBase: string;
}

const DEFAULT_CHAT_CONFIG: ChatConfig = {
  authTokenKey: 'support_auth_token',
  mediaProxyBase: '/api/media',
};

const ChatConfigCtx = createContext<ChatConfig>(DEFAULT_CHAT_CONFIG);

export interface ChatConfigProviderProps {
  value: Partial<ChatConfig>;
  children: ReactNode;
}

/**
 * Provide per-instance config to the chat surface. Partial override — any
 * fields you don't supply fall back to the technician defaults.
 */
export function ChatConfigProvider({ value, children }: ChatConfigProviderProps) {
  const merged: ChatConfig = { ...DEFAULT_CHAT_CONFIG, ...value };
  return <ChatConfigCtx.Provider value={merged}>{children}</ChatConfigCtx.Provider>;
}

/** Read the current chat config. Returns the default values when no provider is present. */
export function useChatConfig(): ChatConfig {
  return useContext(ChatConfigCtx);
}
